import React, { useCallback, useId, useState } from 'react';
import type { ProcessArtifactDto } from '@api/processes';
import { useOptionalEditorContext } from '../useEditorContext';
import {
  ArtifactDownloadButton,
  ArtifactList,
  ArtifactName,
  ArtifactRow,
  ArtifactsMore,
} from './ArtifactsPanel.styled';

const ARTIFACT_DISPLAY_LIMIT = 5;

export interface ArtifactDownloadListProps {
  readonly artifacts: readonly ProcessArtifactDto[];
  /** {@code data-testid} stem (e.g. "process-node__artifacts-{nodeId}-{portId}"). */
  readonly dataTestId: string;
  /**
   * Presigned-URL refresh function. Node-canvas usages read it from the
   * editor context automatically; page-level dialogs mounted OUTSIDE the
   * provider (e.g. the manual-completion modal) must pass it explicitly.
   * When absent, downloads fall back to the artifact's cached URL.
   */
  readonly refreshArtifactUrl?: (artifactId: string) => Promise<string>;
}

/**
 * Compact list of downloadable artifact rows shared between the top
 * {@link ArtifactsPanel} (LIST variant) and the per-output artifact
 * sections inside {@link OutputRow}. Centralises the presigned-URL
 * refresh logic so both surfaces always agree on download behaviour
 * (e.g. silently falling back to the cached URL when refresh fails).
 */
const ArtifactDownloadList: React.FC<ArtifactDownloadListProps> = ({
  artifacts,
  dataTestId,
  refreshArtifactUrl,
}) => {
  const editor = useOptionalEditorContext();
  const refresh = refreshArtifactUrl ?? editor?.refreshArtifactUrl;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const onToggleExpanded = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  }, []);

  const onDownload = useCallback(
    async (artifact: ProcessArtifactDto) => {
      setDownloadingId(artifact.artifactId);
      try {
        const fresh = refresh
          ? await refresh(artifact.artifactId)
          : artifact.downloadUrl;
        if (fresh) window.open(fresh, '_blank', 'noopener,noreferrer');
      } catch {
        if (artifact.downloadUrl) {
          window.open(artifact.downloadUrl, '_blank', 'noopener,noreferrer');
        }
      } finally {
        setDownloadingId(null);
      }
    },
    [refresh],
  );

  if (artifacts.length === 0) return null;

  const hasMore = artifacts.length > ARTIFACT_DISPLAY_LIMIT;
  const visible = expanded
    ? artifacts
    : artifacts.slice(0, ARTIFACT_DISPLAY_LIMIT);

  return (
    <>
      <ArtifactList id={listId} $expanded={expanded} data-testid={dataTestId}>
        {visible.map((a) => (
          <ArtifactRow key={a.artifactId}>
            <ArtifactName title={a.name}>{a.name}</ArtifactName>
            <ArtifactDownloadButton
              type="button"
              className="nodrag"
              onClick={() => {
                onDownload(a).catch(() => undefined);
              }}
              disabled={downloadingId === a.artifactId}
              data-testid={`${dataTestId}__download-${a.artifactId}`}
            >
              {downloadingId === a.artifactId ? 'Loading…' : 'Download'}
            </ArtifactDownloadButton>
          </ArtifactRow>
        ))}
      </ArtifactList>
      {hasMore ? (
        <ArtifactsMore
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-controls={listId}
          data-testid={`${dataTestId}__more`}
        >
          {expanded
            ? 'Show less'
            : `+${artifacts.length - ARTIFACT_DISPLAY_LIMIT} more`}
        </ArtifactsMore>
      ) : null}
    </>
  );
};

export default ArtifactDownloadList;
